/**
 * Enterprise Theme + Shopify Search & Discovery
 *
 * Zeigt auf den Produktkarten automatisch die Variante an,
 * die dem aktuell ausgewählten Farbfilter entspricht.
 *
 * Beispiel:
 * Filter C03 aktiv → alle passenden Produktkarten zeigen C03.
 */

(() => {
  'use strict';

  const COLOR_FILTER_NAME = 'filter.v.t.shopify.color-pattern';
  const PRODUCT_CARD_SELECTOR = 'product-card';
  const VARIANT_INPUT_SELECTOR = 'input.js-option';

  let timer;
  let isUpdating = false;

  /**
   * Liest alle aktuell ausgewählten Farbcodes aus den Filtern.
   */
  function getActiveColors() {
    const checkedFilters = document.querySelectorAll(
      `input[name="${COLOR_FILTER_NAME}"]:checked`
    );

    return Array.from(checkedFilters)
      .map((input) => {
        const filterItem = input.closest('li');

        if (!filterItem) {
          return null;
        }

        /*
         * Bei eurem Filter steht der Farbcode im data-swatch-Attribut,
         * beispielsweise data-swatch="c03".
         */
        const swatch = filterItem.querySelector('[data-swatch]');

        if (swatch?.dataset.swatch) {
          return swatch.dataset.swatch.trim().toUpperCase();
        }

        /*
         * Fallback: sichtbaren Text auslesen und Trefferzahl entfernen.
         * Beispiel: "C03 (3)" wird zu "C03".
         */
        const text = filterItem.innerText
          .replace(/\(\s*\d+\s*\)/g, '')
          .trim();

        return text ? text.toUpperCase() : null;
      })
      .filter(Boolean);
  }

  /**
   * Stellt eine einzelne Produktkarte auf die passende Variante um.
   */
  function updateProductCard(card, activeColors) {
    const variantInputs = Array.from(
      card.querySelectorAll(VARIANT_INPUT_SELECTOR)
    );

    const matchingInput = variantInputs.find((input) => {
      const variantColor = input.value.trim().toUpperCase();
      return activeColors.includes(variantColor);
    });

    if (!matchingInput) {
      return;
    }

    const variantIdentifier =
      matchingInput.dataset.variantId || matchingInput.value;

    /*
     * Verhindert, dass dieselbe Variante durch den MutationObserver
     * immer wieder angeklickt wird.
     */
    if (card.dataset.filteredVariant === variantIdentifier) {
      return;
    }

    card.dataset.filteredVariant = variantIdentifier;

    /*
     * Enterprise besitzt die komplette Varianten-Bildlogik bereits.
     * Durch den Klick verwenden wir genau diese bestehende Funktion.
     */
    matchingInput.click();
  }

  /**
   * Aktualisiert alle sichtbaren Produktkarten.
   */
  function updateAllProductCards() {
    if (isUpdating) {
      return;
    }

    const activeColors = getActiveColors();
    const productCards = document.querySelectorAll(
      PRODUCT_CARD_SELECTOR
    );

    /*
     * Ohne aktiven Farbfilter keine Variante erzwingen.
     * Search & Discovery lädt das Produktraster beim Löschen eines
     * Filters normalerweise neu und stellt die Standardbilder wieder her.
     */
    if (activeColors.length === 0) {
      productCards.forEach((card) => {
        delete card.dataset.filteredVariant;
      });

      return;
    }

    isUpdating = true;

    try {
      productCards.forEach((card) => {
        updateProductCard(card, activeColors);
      });
    } finally {
      window.setTimeout(() => {
        isUpdating = false;
      }, 100);
    }
  }

  /**
   * Mehrere schnelle Änderungen werden zu einer Aktualisierung gebündelt.
   */
  function scheduleUpdate() {
    window.clearTimeout(timer);

    timer = window.setTimeout(() => {
      updateAllProductCards();
    }, 150);
  }

  /*
   * Beim ersten Laden der Seite ausführen.
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleUpdate);
  } else {
    scheduleUpdate();
  }

  /*
   * Search & Discovery ersetzt Filter und Produktraster dynamisch.
   * Der Observer erkennt diese Änderungen und führt die Funktion erneut aus.
   */
  const observer = new MutationObserver(() => {
    scheduleUpdate();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  /*
   * Direkte Änderungen am Farbfilter erkennen.
   */
  document.addEventListener('change', (event) => {
    const target = event.target;

    if (
      target instanceof HTMLInputElement &&
      target.name === COLOR_FILTER_NAME
    ) {
      scheduleUpdate();
    }
  });

  /*
   * Funktioniert auch bei Browser-Zurück und Browser-Vorwärts.
   */
  window.addEventListener('popstate', scheduleUpdate);
})();